import {
  Checkbox,
  Field,
  Input,
  Radio,
  RadioGroup,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import type { IField, ILookupValue } from '@moko/utils';
import { useTranslation } from 'react-i18next';
import { useOtherInfoContext } from '../OtherInfoContext';
import { FieldWrapper, ReadOnlyEmpty } from './FieldWrapper';
import { useFieldReadOnly } from './fieldReadOnly';
import type { FieldComponentProps } from './types';

const selectedIds = (value: unknown): string[] =>
  String(value ?? '')
    .split('|')
    .filter(Boolean);

const groupName = (item: ILookupValue) => item.group?.trim() ?? '';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: 'repeat(auto-fill, minmax(max(10rem, 25%), 1fr))',
    ...shorthands.gap(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    alignItems: 'start',
  },
  group: {
    '& .fui-Field__label': {
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  choice: {
    alignItems: 'flex-start',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: tokens.spacingVerticalXXS,
  },
  price: {
    color: tokens.colorNeutralForeground3,
  },
  name: {
    color: tokens.colorNeutralForeground1,
  },
});

export const PricingItemsField = ({
  field,
  fieldValue,
  isFieldDisabled,
}: FieldComponentProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isReadOnly = useFieldReadOnly(field);
  const { params, handleOtherInfoChange } = useOtherInfoContext();
  const labeled = (name: string, label: string): IField => ({
    ...field,
    name,
    label,
  });
  const items =
    params.metadata.lookups
      ?.find((lookup) => lookup.name === field.lookupList)
      ?.values.filter((item) => item.key) ?? [];
  const selected = selectedIds(fieldValue);

  const groups = new Map<string, ILookupValue[]>();
  const ungrouped: ILookupValue[] = [];
  items.forEach((item) => {
    const name = groupName(item);
    if (!name) {
      ungrouped.push(item);
      return;
    }
    const current = groups.get(name) ?? [];
    current.push(item);
    groups.set(name, current);
  });

  const write = (next: string[]) => {
    handleOtherInfoChange(field.name, next.join('|'));
  };
  const optionLabel = (item: ILookupValue) => (
    <span className={classes.option}>
      <span className={classes.name}>{item.value}</span>
      {item.price ? <span className={classes.price}>{item.price}</span> : null}
    </span>
  );

  if (items.length === 0) return null;

  return (
    <div className={classes.grid}>
      {[...groups.entries()].map(([name, options]) => {
        const chosen = options.find((item) => selected.includes(item.key));
        if (isReadOnly) {
          return (
            <FieldWrapper
              key={name}
              field={labeled(`${field.name}_${name}`, name)}
            >
              {chosen ? (
                <Input
                  value={
                    chosen.price
                      ? `${chosen.value} ${chosen.price}`
                      : chosen.value
                  }
                  readOnly
                  disabled
                />
              ) : (
                <ReadOnlyEmpty />
              )}
            </FieldWrapper>
          );
        }
        return (
          <Field key={name} className={classes.group} label={name}>
            <RadioGroup
              value={chosen?.key ?? ''}
              disabled={isFieldDisabled}
              onChange={(_, data) => {
                const ids = new Set(options.map((item) => item.key));
                write([...selected.filter((id) => !ids.has(id)), data.value]);
              }}
            >
              {options.map((item) => (
                <Radio
                  key={item.key}
                  className={classes.choice}
                  value={item.key}
                  label={{ children: optionLabel(item) }}
                />
              ))}
            </RadioGroup>
          </Field>
        );
      })}
      {ungrouped.map((item) =>
        isReadOnly ? (
          <FieldWrapper
            key={item.key}
            field={labeled(
              `${field.name}_${item.key}`,
              item.price ? `${item.value} ${item.price}` : item.value
            )}
          >
            <Input
              value={
                selected.includes(item.key) ? t('yes', 'Yes') : t('no', 'No')
              }
              readOnly
              disabled
            />
          </FieldWrapper>
        ) : (
          <Checkbox
            key={item.key}
            className={classes.choice}
            label={{ children: optionLabel(item) }}
            checked={selected.includes(item.key)}
            disabled={isFieldDisabled}
            onChange={(_, data) => {
              write(
                data.checked
                  ? [...selected, item.key]
                  : selected.filter((id) => id !== item.key)
              );
            }}
          />
        )
      )}
    </div>
  );
};
